import VanillaFittty from "shared/scripts/vanilla_fittty"
import type { AsyncLoadHooks } from "./async_load"

export const TabManagement = (hooks: Partial<AsyncLoadHooks> = {}) => {
  const allContainer = document.querySelector(".explore-discover-results")
  const serviceContainer = document.querySelector("div.web-services-container")
  const moreStoriesContainer = document.querySelector(".explore-more-stories")
  const tabContainer = document.querySelector(".filter-content-list")

  // Only /explore renders the tab list, and the story category hubs share this bundle for the
  // story-card styles. Everything below manages those tabs, so with no tab list there is nothing
  // to wire up — and dereferencing the missing container would throw before Search() and
  // TrackEvents() ever ran.
  if (!tabContainer) return

  const allTabLinks = tabContainer.querySelectorAll("a")

  const replacePage = (newTitle: string, url: string) => {
    const ogTitleTag = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null

    document.title = newTitle
    if (ogTitleTag) ogTitleTag.content = newTitle
    window.history.replaceState({ url: url }, "", url)
  }

  /**
   * Defer Pretext to the next frame so the tab visibility update can paint first.
   * We bail before queueing rAF when there are no `[data-fittty]` nodes so we do not add an idle
   * frame callback (VanillaFittty would no-op, but still runs its snapshot path). The check inside
   * rAF handles DOM changes between scheduling and running.
   */
  const scheduleFitttyForDiscover = (container: HTMLElement | null | undefined) => {
    if (!container?.querySelector("[data-fittty]")) return

    requestAnimationFrame(() => {
      if (!container.querySelector("[data-fittty]")) return
      void VanillaFittty(container)
    })
  }

  const activateTab = tab => {
    // Both panels are fetched on demand rather than up front, so make sure the
    // one we are about to reveal has been asked for.
    const pending = (tab.dataset.target === "services")
      ? hooks.ensureServices?.()
      : hooks.ensureDiscoveryItems?.()

    // On the views that load tiles on demand the container is still empty at
    // this point, so Pretext has nothing to measure. Those responses are
    // appended with `runFittty` false, so fit again once the request settles or
    // their titles keep the stylesheet default size until the next tab switch.
    const fitDiscover = () => {
      scheduleFitttyForDiscover(allContainer as HTMLElement)
      void pending?.then(() => scheduleFitttyForDiscover(allContainer as HTMLElement))
    }

    allTabLinks.forEach(tab => tab.classList.remove("active"))
    tab.classList.add("active")

    allContainer?.classList.add("hide")
    serviceContainer?.classList.add("hide")

    // Server-rendered only for the stories view, but it sits outside the two containers above, so
    // without this it survives a client-side switch and leaves a stories grid under Services.
    moreStoriesContainer?.classList.toggle("hide", tab.dataset.target !== "stories")

    if (tab.dataset.target == "all") {
      allContainer?.querySelectorAll("div.explore-tile.hide").forEach(tile => tile.classList.remove("hide"))
      allContainer?.classList.remove("hide")

      fitDiscover()
    } else if (tab.dataset.target == "services") {
      serviceContainer?.classList.remove("hide")
    } else { // stories and applets
      allContainer?.querySelectorAll("div.explore-tile").forEach(tile => {
        if (tile.dataset.type == tab.dataset.target) {
          tile.classList.remove("hide")
        } else {
          tile.classList.add("hide")
        }
      })

      allContainer?.classList.remove("hide")

      if (tab.dataset.target == "applets") {
        fitDiscover()
      }
    }

    replacePage(tab.dataset.title, tab.href)
  }

  allTabLinks.forEach(tab => {
    tab.addEventListener("click", (event) => {
      event.preventDefault()
      const tab = event.target.closest("a")

      if (tab.classList.contains("active")) return

      activateTab(tab)
    })
  })
}
